import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';

interface CreatableTagsProps {
    values: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    tagType?: 'hashtag' | 'usertag' | null;
    noOptionsMessage?: string;
    disabled?: boolean;
    [key: string]: any;
}

const isValidHandle = (value: string): boolean => {
    const clean = value.replace(/^@/, '');
    return /^[a-zA-Z0-9._]{2,30}$/.test(clean);
};


const extractUsernameFromUrl = (input: string): string | null => {
    try {
        const url = new URL(input.trim());
        const host = url.hostname.replace('www.', '');
        const path = url.pathname.replace(/^\/|\/$/g, '');
        const segments = path.split('/');

        // Instagram → /username
        if (
            host === 'instagram.com' &&
            segments.length === 1 &&
            !['reel', 'p', 'tv', 'stories', 'explore'].includes(segments[0])
        ) {
            return segments[0];
        }

        // Facebook → /username
        if (
            host === 'facebook.com' &&
            segments.length === 1 &&
            !['watch', 'reel', 'stories', 'pages', 'groups'].includes(segments[0])
        ) {
            return segments[0];
        }

        // YouTube
        if (host === 'youtube.com') {
            if (segments[0]?.startsWith('@')) {
                return segments[0].replace('@', '');
            }
            if (['c', 'user'].includes(segments[0]) && segments.length === 2) {
                return segments[1];
            }
        }

        return null;
    } catch {
        return null;
    }
};


const formatTag = (input: string, tagType: 'hashtag' | 'usertag'): string => {
    if (!input.trim()) return '';

    let clean = input.trim().replace(/^[@#]+/, '');

    if (tagType === 'usertag' && !isValidHandle(clean)) {
        const extracted = extractUsernameFromUrl(clean);
        if(!extracted){
            toast.error(`Invalid handle: ${input}`);
            return "";
        }
        if (extracted) clean = extracted;
    }

    const prefix = tagType === 'hashtag' ? '#' : '@';
    return clean ? `${prefix}${clean}` : '';
};

const isValidHashtag = (input: string): boolean => {
    return /^[a-zA-Z0-9_]{1,50}$/.test(input);
};

const CreatableTags: React.FC<CreatableTagsProps> = ({
    values,
    onChange,
    tagType = null,
    noOptionsMessage = 'Type to add...',
    placeholder = 'Type tags...',
    disabled = false,
    ...rest
}) => {

    const handleCreate = (inputValue: string) => {
        if(tagType === 'hashtag' && !isValidHashtag(inputValue)) {
            toast.error(`Hashtags can only contain letters, numbers, and underscores.`);
            return;
        }
        const splitTags = inputValue
            .split(/[\s,]+/) // Split on space, comma, newline
            .map((tag) => tagType ? formatTag(tag, tagType) : tag)
            .filter(tag => tag && !values.includes(tag));

        onChange([...values, ...splitTags]);
    };

    const handleChange = (selected: any) => {
        const tags = selected?.map((opt: any) => tagType ? formatTag(opt.value, tagType) : opt.value) || [];
        onChange(tags);
    };

    const handleRemove = (tagToRemove: string) => {
        onChange(values.filter(tag => tag !== tagToRemove));
    };

    return (
        <>
            {
                !disabled &&
                <CreatableSelect
                    {...rest}
                    isDisabled={disabled}
                    placeholder={placeholder}
                    isMulti
                    classNamePrefix={'influns'}
                    styles={{
                        multiValueLabel: (base: any) => ({
                            ...base,
                            color: 'white',
                        }),
                    }}
                    noOptionsMessage={() => noOptionsMessage}
                    onCreateOption={handleCreate}
                    onChange={handleChange}
                    value={null}
                />
            }

            <div className="selected-multi-values mt-2">
                {values.map((tag, i) => (
                    <span key={i} className="selected-value-badge">
                        {tag}
                        {
                            !disabled &&
                            <span className="cursor-pointer" onClick={() => handleRemove(tag)}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="5.80549" cy="5.80549" r="4.80549" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M7.3125 7.3125L4.3125 4.3125" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M4.3125 7.3125L7.3125 4.3125" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        }
                    </span>
                ))}
                {
                    disabled && values.length === 0 &&
                    '-'
                }
            </div>
        </>
    );
};

export default CreatableTags;